import { get, post } from "./http.service";

const trace = async (step, operation) => {
  const log = fields => {
    if (process.env.NODE_ENV === "development") console.info("[Import]", { step, ...fields });
  };
  log({ outcome: "started" });
  try {
    const result = await operation();
    log({ outcome: "succeeded" });
    return result;
  } catch (error) {
    const status = error && (error.status || (error.response && error.response.status));
    log({ outcome: "failed", status: typeof status === "number" ? status : null });
    throw error;
  }
};

const createReservation = (body, idempotencyKey) =>
  trace("reservation_request", () => post("import/upload-reservations", body, { headers: { "Idempotency-Key": idempotencyKey }, preserveHttpError: true }));
const getReservation = reservationId => get(`import/upload-reservations/${reservationId}`, { preserveHttpError: true });
const getActiveRun = () => get("import/runs/active", { preserveHttpError: true });
const getRun = runId => get(`import/runs/${runId}`, { preserveHttpError: true });
const getContinuation = continuationRef =>
  get(`import/continuations/${encodeURIComponent(continuationRef)}`, { preserveHttpError: true });

const uploadToS3 = (upload, file, onProgress) => trace("s3_upload", async () => {
  const response = await fetch(upload.url, { method: "PUT", headers: upload.headers, body: file });
  if (!response.ok) throw new Error("The workbook upload could not be completed.");
  if (onProgress) onProgress(100);
  return response;
});

const proceed = (runId, idempotencyKey) =>
  post(`import/runs/${runId}/proceed`, {}, { headers: { "Idempotency-Key": idempotencyKey }, preserveHttpError: true });
const cancel = (runId, idempotencyKey) =>
  post(`import/runs/${runId}/cancel`, {}, { headers: { "Idempotency-Key": idempotencyKey }, preserveHttpError: true });
const complete = (runId, idempotencyKey) =>
  post(`import/runs/${runId}/complete`, {}, { headers: { "Idempotency-Key": idempotencyKey }, preserveHttpError: true });
const getArtifact = (runId, artifactType, signal) =>
  get(`import/runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(artifactType)}`, {
    signal,
    retry: 0,
    preserveHttpError: true,
  });

const downloadArtifact = async (runId, artifactType, signal, renewed = false) => {
  const path = `import/runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(artifactType)}/content`;
  const parts = [];
  try {
    const grant = await getArtifact(runId, artifactType, signal);
    const expectedPath = `/api/${path}`;
    if (
      grant.transport !== "authenticated-chunks" ||
      ![
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv; charset=utf-8",
      ].includes(grant.contentType) ||
      typeof grant.url !== "string" ||
      !grant.url.startsWith(`${expectedPath}?`) ||
      grant.chunkBytes !== 1048576 ||
      !Number.isSafeInteger(grant.totalBytes) ||
      grant.totalBytes < 0
    ) {
      throw new Error("The download response is invalid.");
    }
    const descriptor = new URLSearchParams(grant.url.slice(expectedPath.length + 1)).get("descriptor");
    let offset = 0;
    let more = true;
    while (more) {
      if (signal && signal.aborted) throw new Error("Download cancelled.");
      // The URL is constructed locally; never forward credentials to a grant URL.
      // eslint-disable-next-line no-await-in-loop
      const chunk = await get(path, { params: { descriptor, offset }, signal, retry: 0, preserveHttpError: true });
      if (signal && signal.aborted) throw new Error("Download cancelled.");
      if (chunk.offset !== offset || chunk.totalBytes !== grant.totalBytes || typeof chunk.dataBase64 !== "string") {
        throw new Error("The download response is invalid.");
      }
      const decoded = window.atob(chunk.dataBase64);
      const expectedLength = Math.min(grant.chunkBytes, grant.totalBytes - offset);
      const next = offset + expectedLength;
      if (decoded.length !== expectedLength || chunk.nextOffset !== (next < grant.totalBytes ? next : null)) {
        throw new Error("The download is incomplete.");
      }
      parts.push(Uint8Array.from(decoded, character => character.charCodeAt(0)));
      offset = next;
      more = chunk.nextOffset !== null;
    }
    return {
      filename: grant.filename,
      blob: new Blob(parts, { type: grant.contentType }),
    };
  } catch (error) {
    parts.length = 0;
    if (!renewed && !(signal && signal.aborted) && error.response && error.response.status === 409) {
      return downloadArtifact(runId, artifactType, signal, true);
    }
    throw error;
  }
};

export default {
  cancel,
  complete,
  createReservation,
  downloadArtifact,
  getActiveRun,
  getArtifact,
  getContinuation,
  getReservation,
  getRun,
  proceed,
  uploadToS3,
};
